import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Linkedin, Download, X } from "lucide-react";
import profile from "../../../data/profile.json";

/**
 * Hero v3 — 50/50 split (text left, video right). Compact, ~70vh max desktop.
 * Mobile stacks: text on top, video below.
 */
export default function Hero() {
  const { headline, subline } = profile.identity;
  const parts = splitHeadline(headline);
  const videoSrc = profile.heroVideoUrl;

  const [lightbox, setLightbox] = useState(false);
  const inlineRef = useRef(null);

  return (
    <section
      className="rp-section relative"
      style={{ background: "#EDE7D9" }}
      data-testid="hero-simple"
    >
      <div
        className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-2 md:pt-3 pb-4 md:pb-6 grid grid-cols-1 md:grid-cols-[1.05fr_1fr] gap-6 md:gap-5 items-start"
      >
        {/* LEFT — headline + tagline */}
        <div className="text-left" data-testid="hero-text">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="rp-display font-light leading-[1.06] text-[#262016]"
            style={{
              fontSize: "clamp(2.1rem, 4.4vw, 3.6rem)",
              textWrap: "balance",
            }}
            data-testid="hero-headline"
          >
            {parts.map((p, i) =>
              p.accent ? (
                <em
                  key={i}
                  className="rp-italic font-medium"
                  style={{ color: p.color, fontStyle: "italic" }}
                >
                  {p.text}
                </em>
              ) : (
                <span key={i}>{p.text}</span>
              )
            )}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 md:mt-4 text-sm md:text-[15px] font-light leading-relaxed max-w-md"
            style={{
              color: "#8A7E6B",
              fontFamily: "'Space Grotesk', system-ui, sans-serif",
              textWrap: "pretty",
            }}
            data-testid="hero-tagline-line1"
          >
            15 years across business, corporate, design and AI.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2 md:mt-2.5 rp-display rp-italic font-medium leading-tight inline-block"
            style={{
              color: "#262016",
              fontStyle: "italic",
              fontSize: "clamp(1.15rem, 1.55vw, 1.35rem)",
              textWrap: "balance",
              borderBottom: "2px solid #B5793F",
              paddingBottom: "2px",
            }}
            data-testid="hero-tagline-signature"
          >
            {subline && subline.includes(".")
              ? subline.split(".").slice(1).join(".").trim() || "Your go-to human in the loop."
              : "Your go-to human in the loop."}
          </motion.p>

          {/* Prominent CTAs — LinkedIn + Download Resume */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.42 }}
            className="mt-4 md:mt-5 flex flex-wrap items-center gap-2.5"
            data-testid="hero-utility-links"
          >
            <a
              href={profile.identity.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#26201625] hover:border-[#262016] hover:bg-[#262016] hover:text-[#EDE7D9] text-[#262016] transition-all"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: "0.92rem",
                fontWeight: 500,
              }}
              data-testid="hero-linkedin"
            >
              <Linkedin size={16} strokeWidth={1.8} />
              LinkedIn
            </a>
            <a
              href="/resume"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[#EDE7D9] transition-all hover:opacity-90"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: "0.92rem",
                fontWeight: 500,
                background: "#262016",
                boxShadow: "0 6px 16px -8px rgba(38,32,22,0.5)",
              }}
              data-testid="hero-resume"
            >
              <Download size={16} strokeWidth={1.8} />
              Download Resume
            </a>
          </motion.div>
        </div>

        {/* RIGHT — hero intro video */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="w-full"
          data-testid="hero-video"
        >
          <button
            type="button"
            onClick={() => setLightbox(true)}
            aria-label="Play portfolio intro with sound"
            data-testid="hero-video-trigger"
            className="group relative w-full aspect-video rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#B5793F]/60"
            style={{
              background: "#1A1610",
              boxShadow: "0 24px 60px -24px rgba(38,32,22,0.45), 0 6px 18px rgba(38,32,22,0.12)",
              border: "1px solid rgba(38,32,22,0.10)",
            }}
          >
            <video
              ref={inlineRef}
              src={videoSrc}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              controlsList="nodownload"
              disablePictureInPicture
              onContextMenu={e => e.preventDefault()}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* subtle bottom vignette */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1A1610]/40 via-transparent to-transparent" />

            {/* Centered play icon — indicates it's a video */}
            <span
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              aria-hidden="true"
            >
              <span
                className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
                style={{
                  background: "rgba(237,231,217,0.92)",
                  boxShadow: "0 12px 32px -8px rgba(0,0,0,0.35)",
                }}
              >
                <Play size={22} className="ml-1 text-[#262016]" strokeWidth={1.6} />
              </span>
            </span>
          </button>
        </motion.div>
      </div>

      {/* Lightbox — full experience with audio */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            style={{ background: "rgba(20,16,10,0.88)", backdropFilter: "blur(10px)" }}
            role="dialog"
            aria-modal="true"
            data-testid="hero-video-modal"
            onClick={() => setLightbox(false)}
          >
            <button
              type="button"
              onClick={() => setLightbox(false)}
              data-testid="hero-video-close"
              aria-label="Close video"
              className="absolute top-5 right-5 md:top-7 md:right-7 w-11 h-11 rounded-full flex items-center justify-center text-[#262016]"
              style={{ background: "#EDE7D9" }}
            >
              <X size={20} />
            </button>
            <div
              className="relative w-full max-w-5xl aspect-video rounded-xl overflow-hidden"
              style={{ background: "#0a0a0a" }}
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={videoSrc}
                controls
                controlsList="nodownload"
                disablePictureInPicture
                onContextMenu={e => e.preventDefault()}
                autoPlay
                playsInline
                className="w-full h-full"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// --- helpers ---
function splitHeadline(sentence) {
  const specs = [
    { phrase: "business growth", color: "#23C795" },
    { phrase: "design thinking", color: "#B5793F" },
    { phrase: "AI",              color: "#19B5DE" },
  ];
  const marked = specs
    .map((s) => ({ ...s, idx: sentence.toLowerCase().indexOf(s.phrase.toLowerCase()) }))
    .filter((s) => s.idx >= 0)
    .sort((a, b) => a.idx - b.idx);

  const out = [];
  let cursor = 0;
  marked.forEach((s) => {
    if (s.idx > cursor) out.push({ text: sentence.slice(cursor, s.idx), accent: false });
    out.push({ text: sentence.slice(s.idx, s.idx + s.phrase.length), accent: true, color: s.color });
    cursor = s.idx + s.phrase.length;
  });
  if (cursor < sentence.length) out.push({ text: sentence.slice(cursor), accent: false });
  return out;
}
